import {
  PubnubService,
  PubsubReceivedStatusEvent,
  PubsubReceivedMessageEvent,
} from '@getvim/vim-connect-pubsub';
import { agentAuthenticate, AgentAuthenticateResponse } from '../logic/agentAuthenticate';
import { useEffect, useState } from 'react';
import { Tab } from '../types';
import { createLogger } from '../utils';

const logger = createLogger('usePubSubService');

let pubsubService;

function shutdownPubSubService(): void {
  if (pubsubService) {
    try {
      logger.info('Shutdown PubSubService', { noPHI: true });
      void pubsubService.pubnub.unsubscribeAll();
      pubsubService = undefined;
    } catch (error) {
      logger.error('Error shutdown PubSubService', { error, noPHI: true });
    }
  }
}

export const usePubSubService = () => {
  const [srn, setSrn] = useState<string>('');

  async function initPubSubService({
    pubsubId,
    organizationId,
  }: {
    pubsubId: string;
    organizationId: number | undefined;
  }): Promise<void> {
    if (!pubsubService) {
      try {
        logger.info('Start init PubSubService', { noPHI: true, pubsubId, organizationId });
        if (!organizationId) {
          logger.error('Organization ID is required', { noPHI: true });
          return;
        }
        const result = await agentAuthenticate({
          id: pubsubId,
          organizationId: organizationId,
        });

        const { config, token, organization, incomeChannel } = result;

        pubsubService = new PubnubService({
          authKey: token,
          ...config,
          uuid: pubsubId,
        });
        logger.info('Success init PubSubService', {
          noPHI: true,
          pubsubId,
          organizationId,
          incomeChannel,
        });

        pubsubService.addListener({
          status: (event: PubsubReceivedStatusEvent) => {
            logger.info('Received PubSub status', { event });
          },
          message: async (event: PubsubReceivedMessageEvent) => {
            logger.info('Received new PubSub message', {
              noPHI: true,
            });
            const { message } = event;
            const decryptMessage = pubsubService?.decrypt({
              cipherKey: organization.cipherKey!,
              secureText: `${message}`,
            });
            const { responseChannel, actionData } = decryptMessage;
            logger.info('Decrypted PubSub message', {
              noPHI: true,
              responseChannel,
              actionData, // remove after debug
            });
            if (!actionData?.srn) {
              logger.warning('No SRN found in PubSub message', {
                noPHI: true,
                actionData,
              });
              return;
            }
            setSrn(actionData?.srn);
            const encrypted = pubsubService?.encrypt({
              cipherKey: organization.cipherKey!,
              clearText: JSON.stringify({ actionId: actionData?.actionId }),
            });

            logger.info('Got encrypted PubSub message', {
              noPHI: true,
              actionId: actionData?.actionId,
            });
            try {
              await pubsubService?.publish({
                channel: responseChannel,
                message: { encrypted: encrypted!, orgSlug: organization.slug },
              });
              logger.info('Published PubSub response message', {
                noPHI: true,
                actionId: actionData?.actionId,
              });
            } catch (error) {
              logger.error('Error publish PubSub response message', { error, noPHI: true });
            }
          },
        });

        pubsubService.subscribe({ channels: [incomeChannel] });
      } catch (error) {
        logger.error('Error init PubSubService', { error, noPHI: true });
      }
    }
  }

  useEffect(() => {
    return () => {
      shutdownPubSubService();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { srn, initPubSubService, shutdownPubSubService };
};
